// src/components/FilterPanel.tsx
import * as React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";

// Filter state type for all categories
export interface FilterState {
  formats: string[];
  performance: string[];
  businessModels: string[];
  industries: string[];
  topics: string[];
}

export interface FilterPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  // Current filter values
  filters: FilterState;

  // Options for each category
  formatOptions?: string[];
  performanceOptions?: string[];
  businessModelOptions?: string[];
  industryOptions?: string[]; // Maps to existing categories
  topicOptions?: string[]; // Maps to existing tags

  // Callbacks
  onApply: (filters: FilterState) => void;
  onClear: () => void;
}

// Default format options (Static/Video based on common ad types)
const DEFAULT_FORMAT_OPTIONS = ["Static", "Video"];

// Default performance options (based on how long ads have been running)
const DEFAULT_PERFORMANCE_OPTIONS = [
  "Early Results (<30 days live)",
  "Promising (30-90 days live)",
  "Solid (90-180 days live)",
  "Top Tier (>180 days live)",
];

// Default business model options
const DEFAULT_BUSINESS_MODEL_OPTIONS = ["B2B", "B2C", "DTC", "Marketplace"];

// Filter section component for reusability
interface FilterSectionProps {
  title: string;
  options: string[];
  selected: Set<string>;
  onToggle: (option: string) => void;
  columns?: 1 | 2 | 3;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  options,
  selected,
  onToggle,
  columns = 2,
}) => {
  if (options.length === 0) return null;

  // Responsive grid: single column on very small screens, then respect columns prop
  const gridColsMap = {
    1: "grid-cols-1",
    2: "grid-cols-1 min-[400px]:grid-cols-2",
    3: "grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3",
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <div className={cn("grid gap-2", gridColsMap[columns])}>
        {options.map((option) => {
          const isChecked = selected.has(option);
          const id = `filter-${title.toLowerCase().replace(/\s+/g, "-")}-${option.toLowerCase().replace(/\s+/g, "-")}`;

          return (
            <label
              key={option}
              htmlFor={id}
              className={cn(
                // Improved touch targets: min-height 44px for accessibility
                "flex items-center gap-3 p-3 rounded-lg cursor-pointer",
                "min-h-[44px]", // Touch-friendly minimum size
                "transition-colors duration-150",
                "hover:bg-accent/50 active:bg-accent/60",
                // Touch feedback
                "touch-manipulation",
                isChecked && "bg-accent/70"
              )}
            >
              <Checkbox
                id={id}
                checked={isChecked}
                onCheckedChange={() => onToggle(option)}
                // Larger checkbox for touch
                className="h-5 w-5 shrink-0 border-muted-foreground/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <span className="text-sm text-foreground leading-tight">{option}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export const FilterPanel: React.FC<FilterPanelProps> = ({
  open,
  onOpenChange,
  filters,
  formatOptions = DEFAULT_FORMAT_OPTIONS,
  performanceOptions = DEFAULT_PERFORMANCE_OPTIONS,
  businessModelOptions = DEFAULT_BUSINESS_MODEL_OPTIONS,
  industryOptions = [],
  topicOptions = [],
  onApply,
  onClear,
}) => {
  // Draft state for selections (only applied on "Apply")
  const [draftFilters, setDraftFilters] = React.useState<FilterState>(filters);

  // Sync draft state when filters prop changes or panel opens
  React.useEffect(() => {
    if (open) {
      setDraftFilters(filters);
    }
  }, [open, filters]);

  // Convert arrays to sets for O(1) lookups
  const formatSet = React.useMemo(() => new Set(draftFilters.formats), [draftFilters.formats]);
  const performanceSet = React.useMemo(() => new Set(draftFilters.performance), [draftFilters.performance]);
  const businessModelSet = React.useMemo(() => new Set(draftFilters.businessModels), [draftFilters.businessModels]);
  const industrySet = React.useMemo(() => new Set(draftFilters.industries), [draftFilters.industries]);
  const topicSet = React.useMemo(() => new Set(draftFilters.topics), [draftFilters.topics]);

  // Count total active filters in draft
  const activeFilterCount =
    draftFilters.formats.length +
    draftFilters.performance.length +
    draftFilters.businessModels.length +
    draftFilters.industries.length +
    draftFilters.topics.length;

  // Toggle handlers
  const createToggleHandler = (
    key: keyof FilterState,
    currentSet: Set<string>
  ) => (option: string) => {
    const next = new Set(currentSet);
    if (next.has(option)) {
      next.delete(option);
    } else {
      next.add(option);
    }
    setDraftFilters((prev) => ({
      ...prev,
      [key]: Array.from(next),
    }));
  };

  const handleApply = () => {
    onApply(draftFilters);
    onOpenChange(false);
  };

  const handleClear = () => {
    const emptyFilters: FilterState = {
      formats: [],
      performance: [],
      businessModels: [],
      industries: [],
      topics: [],
    };
    setDraftFilters(emptyFilters);
    onClear();
    onOpenChange(false);
  };

  const handleCancel = () => {
    // Reset draft to original filters
    setDraftFilters(filters);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          // Full-screen on mobile, max-width on larger screens
          "!w-full sm:!w-[400px] sm:!max-w-md",
          // Full height with safe area padding for mobile devices
          "h-full max-h-[100dvh]",
          "bg-background/95 backdrop-blur-xl",
          "border-l border-white/10",
          "flex flex-col p-0",
          // Hide the default close button from Sheet component
          "[&>button]:hidden"
        )}
      >
        {/* Header - larger touch targets on mobile */}
        <SheetHeader className="px-4 sm:px-6 py-4 border-b border-white/10 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-5 w-5 text-muted-foreground" />
              <SheetTitle className="text-lg font-semibold">Filters</SheetTitle>
              {activeFilterCount > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-primary/20 text-primary rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </div>
            {/* Close button with touch-friendly size */}
            <button
              type="button"
              onClick={handleCancel}
              className={cn(
                "flex items-center justify-center",
                "h-10 w-10 -mr-2", // Touch-friendly 44px target with negative margin to align edge
                "rounded-full",
                "opacity-70 transition-opacity",
                "hover:opacity-100 hover:bg-accent/50",
                "active:opacity-100 active:bg-accent/60",
                "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                "touch-manipulation"
              )}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </SheetHeader>

        {/* Scrollable Filter Content - with momentum scrolling on iOS */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 space-y-6 -webkit-overflow-scrolling-touch">
          {/* Format Section - commented out for now
          {formatOptions.length > 0 && (
            <FilterSection
              title="Format"
              options={formatOptions}
              selected={formatSet}
              onToggle={createToggleHandler("formats", formatSet)}
              columns={2}
            />
          )}
          */}

          {/* Performance Section - commented out for now
          {performanceOptions.length > 0 && (
            <FilterSection
              title="Performance"
              options={performanceOptions}
              selected={performanceSet}
              onToggle={createToggleHandler("performance", performanceSet)}
              columns={1}
            />
          )}
          */}

          {/* Business Model Section - commented out for now
          {businessModelOptions.length > 0 && (
            <FilterSection
              title="Business Model"
              options={businessModelOptions}
              selected={businessModelSet}
              onToggle={createToggleHandler("businessModels", businessModelSet)}
              columns={2}
            />
          )}
          */}

          {/* Industry Section (uses existing categories) */}
          {industryOptions.length > 0 && (
            <FilterSection
              title="Industry"
              options={industryOptions}
              selected={industrySet}
              onToggle={createToggleHandler("industries", industrySet)}
              columns={2}
            />
          )}

          {/* Topic Section (uses existing tags) */}
          {topicOptions.length > 0 && (
            <FilterSection
              title="Topic"
              options={topicOptions}
              selected={topicSet}
              onToggle={createToggleHandler("topics", topicSet)}
              columns={2}
            />
          )}

          {/* Empty state if no filter options */}
          {formatOptions.length === 0 &&
            performanceOptions.length === 0 &&
            businessModelOptions.length === 0 &&
            industryOptions.length === 0 &&
            topicOptions.length === 0 && (
              <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                No filter options available
              </div>
            )}
        </div>

        {/* Footer with Apply/Clear buttons - safe area padding for mobile */}
        <SheetFooter className={cn(
          "px-4 sm:px-6 py-4 border-t border-white/10",
          "pb-[max(1rem,env(safe-area-inset-bottom))]", // Safe area for iPhone notch
          "gap-3 shrink-0",
          // Stack on mobile, row on larger screens
          "flex-col-reverse sm:flex-row"
        )}>
          <Button
            variant="outline"
            onClick={handleClear}
            disabled={activeFilterCount === 0}
            className={cn(
              "w-full sm:w-auto sm:flex-none",
              "h-12 sm:h-10", // Taller on mobile for touch
              "text-base sm:text-sm",
              "touch-manipulation"
            )}
          >
            Clear All
          </Button>
          <Button
            variant="default"
            onClick={handleApply}
            className={cn(
              "w-full sm:w-auto sm:flex-none",
              "h-12 sm:h-10", // Taller on mobile for touch
              "text-base sm:text-sm",
              "touch-manipulation"
            )}
          >
            Apply Filters
            {activeFilterCount > 0 && (
              <span className="ml-1">({activeFilterCount})</span>
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

// Export a helper hook for managing filter state
export function useFilterPanel(initialFilters?: Partial<FilterState>) {
  const [open, setOpen] = React.useState(false);
  const [filters, setFilters] = React.useState<FilterState>({
    formats: [],
    performance: [],
    businessModels: [],
    industries: [],
    topics: [],
    ...initialFilters,
  });

  const handleApply = React.useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
  }, []);

  const handleClear = React.useCallback(() => {
    setFilters({
      formats: [],
      performance: [],
      businessModels: [],
      industries: [],
      topics: [],
    });
  }, []);

  const activeFilterCount =
    filters.formats.length +
    filters.performance.length +
    filters.businessModels.length +
    filters.industries.length +
    filters.topics.length;

  return {
    open,
    setOpen,
    filters,
    setFilters,
    handleApply,
    handleClear,
    activeFilterCount,
  };
}

export default FilterPanel;
